// src/models/VacationReportModel.js

/**
 * VacationReportModel
 * Represents a single vacation day entry used for report display and CSV export.
 * Follows the same structural pattern as TimeEntryReportModel.
 *
 * Source collection: companies/{companyId}/vacations/{vacationId}
 *
 * Note: staffName is not stored in Firestore and must be resolved
 * externally via the staffMembers array before constructing this model.
 *
 * Note: durationMinutes is intentionally excluded from this model.
 * Vacation reports show exact dates only. Each Firestore document
 * represents one vacation day regardless of its durationMinutes value.
 */
export class VacationReportModel {
  /**
   * @param {string} staffId - Firestore staff document ID
   * @param {string} staffName - Resolved staff name (not stored in Firestore)
   * @param {string} date - Vacation date as YYYY-MM-DD string, converted from Firestore Timestamp
   */
  constructor(staffId, staffName, date) {
    this.staffId = staffId;
    this.staffName = staffName || '';
    this.date = date; // YYYY-MM-DD string
  }

  // ---------------------------------------------------------------------------
  // Display Methods
  // ---------------------------------------------------------------------------

  /**
   * Returns formatted date string in DD.MM.YYYY format.
   * Parses the stored YYYY-MM-DD string directly to avoid UTC timezone shifts
   * that would occur when passing the string to new Date().
   */
  getFormattedDate() {
    if (!this.date) return '';
    const [year, month, day] = this.date.split('-');
    return `${day}.${month}.${year}`;
  }

  // ---------------------------------------------------------------------------
  // Static Factory
  // ---------------------------------------------------------------------------

  /**
   * Creates a VacationReportModel from a VacationModel instance.
   * staffName must be supplied externally as it is not stored in Firestore.
   *
   * @param {Object} vacationModel - Source VacationModel instance
   * @param {string} staffId - Firestore staff document ID
   * @param {string} staffName - Resolved staff name from the staffMembers array
   * @returns {VacationReportModel}
   */
  static fromVacationModel(vacationModel, staffId, staffName) {
    return new VacationReportModel(
      staffId,
      staffName || '',
      vacationModel.date
    );
  }

  // ---------------------------------------------------------------------------
  // Static Utilities
  // ---------------------------------------------------------------------------

  /**
   * Groups an array of VacationReportModel entries by staffId.
   * Returns a plain object keyed by staffId where each value is an array
   * of that staff member's entries sorted by date ascending.
   *
   * @param {VacationReportModel[]} entries
   * @returns {Object.<string, VacationReportModel[]>}
   */
  static groupByStaff(entries) {
    const groups = entries.reduce((acc, entry) => {
      if (!acc[entry.staffId]) {
        acc[entry.staffId] = [];
      }
      acc[entry.staffId].push(entry);
      return acc;
    }, {});

    Object.keys(groups).forEach(staffId => {
      groups[staffId].sort((a, b) => a.date.localeCompare(b.date));
    });

    return groups;
  }

  /**
   * Builds one summary row per staff member for the Vacation Summary report type.
   * Each row contains the staff name and total vacation day count.
   * Every entry counts as one day. Duration is not tracked in vacation reports.
   * Rows are sorted alphabetically by staffName for consistent output.
   *
   * @param {VacationReportModel[]} entries
   * @returns {Array<{
   *   staffId: string,
   *   staffName: string,
   *   totalDays: number
   * }>}
   */
  static buildSummaryRows(entries) {
    const grouped = VacationReportModel.groupByStaff(entries);

    const rows = Object.entries(grouped).map(([staffId, staffEntries]) => {
      const staffName = staffEntries[0]?.staffName || '';
      const totalDays = staffEntries.length;

      return {
        staffId,
        staffName,
        totalDays
      };
    });

    return rows.sort((a, b) => a.staffName.localeCompare(b.staffName));
  }
}

export default VacationReportModel;